import type { Conversation } from '@/features/conversation';
import type { Message } from '@/features/messages';
import type { FriendOnlineStatusDto, FriendPresence } from '@/features/participant';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * UI Store - Quản lý UI state
 * Chỉ chứa: layout preferences, temporary UI state
 */

interface UIState {
  // Layout
  sidebarExpanded: boolean;
  asideExpanded: boolean;

  // Message search drawer
  messageSearchDrawerOpen: boolean;

  // Friends with presence
  friends: FriendPresence[];

  // Active conversation
  activeConversation: Nullable<Conversation>;
  activeBubbleConvs: Conversation[]; // Lưu các conv bubble đã mở

  // Message interactions
  replyMessages: Record<string, Message>; // conversationId -> Message

  // Typing indicators
  typingUsers: Record<string, Set<string>>; // conversationId -> Set<userId>
}

interface UIActions {
  // Layout
  toggleSidebar: () => void;
  toggleAside: () => void;
  setSidebarExpanded: (expanded: boolean) => void;

  // Message search drawer
  toggleMessageSearchDrawer: () => void;
  setMessageSearchDrawerOpen: (open: boolean) => void;

  // Friends with presence
  setFriends: (friends: FriendPresence[]) => void;
  updateFriendPresence: (friend: FriendOnlineStatusDto) => void;
  isOnline: (userId: string) => boolean;

  // Active conversation
  setActiveConversation: (conversation: Nullable<Conversation>) => void;

  // Reply messages
  setReplyMessage: (conversationId: string, message: Message) => void;
  getReplyMessage: (conversationId: string) => Message | undefined;
  clearReplyMessage: (conversationId?: string) => void;

  // Typing indicators
  addTypingUser: (conversationId: string, userId: string) => void;
  removeTypingUser: (conversationId: string, userId: string) => void;
  clearTypingUsers: (conversationId: string) => void;

  addActiveBubbleConv: (conversation: Conversation) => void;
  removeActiveBubbleConv: (conversationId: string) => void;
  clearActiveBubbleConvs: () => void;

  // Reset default state
  reset: () => void;
}

type UIStore = UIState & UIActions;

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      // State
      sidebarExpanded: true,
      asideExpanded: false,
      messageSearchDrawerOpen: false,
      activeConversation: null,
      replyMessages: {},
      typingUsers: {},
      activeBubbleConvs: [],
      friends: [],

      // Layout actions
      toggleSidebar: () => {
        set((state) => ({ sidebarExpanded: !state.sidebarExpanded }));
      },

      toggleAside: () => {
        set((state) => ({ asideExpanded: !state.asideExpanded }));
      },

      setSidebarExpanded: (expanded) => {
        set({ sidebarExpanded: expanded });
      },

      // Message search drawer actions
      toggleMessageSearchDrawer: () => {
        set((state) => ({ messageSearchDrawerOpen: !state.messageSearchDrawerOpen }));
      },

      setMessageSearchDrawerOpen: (open) => {
        set({ messageSearchDrawerOpen: open });
      },

      // Friends with presence
      setFriends: (friends) => {
        set({ friends });
      },

      updateFriendPresence: (friend) => {
        set((state) => {
          const index = state.friends.findIndex((f) => f.userId === friend.friendId);
          if (index !== -1) {
            const updatedFriends = [...state.friends];
            const wasOnline = state.friends[index].isOnline;
            const isNowOnline = friend.status === 'ONLINE';
            updatedFriends[index] = { ...state.friends[index], isOnline: isNowOnline };

            console.info('[UI Store] Friend presence updated:', {
              friendId: friend.friendId,
              status: friend.status,
              wasOnline,
              isNowOnline,
              changed: wasOnline !== isNowOnline
            });

            return { friends: updatedFriends };
          }
          console.warn('[UI Store] Friend not found for presence update:', friend.friendId);
          return state;
        });
      },

      isOnline: (userId) => {
        return get().friends.some((f) => f.userId === userId && f.isOnline);
      },

      // Active conversation
      setActiveConversation: (conversation) => {
        const state = get();
        set({
          activeConversation: conversation,
          messageSearchDrawerOpen: false,
          asideExpanded:
            state.asideExpanded && conversation?.conversationId === state.activeConversation?.conversationId
        });
      },
      addActiveBubbleConv: (conversation) => {
        set((state) => ({
          activeBubbleConvs: state.activeBubbleConvs.some((c) => c.conversationId === conversation.conversationId)
            ? state.activeBubbleConvs
            : [...state.activeBubbleConvs, conversation]
        }));
      },
      removeActiveBubbleConv: (conversationId) => {
        set((state) => ({
          activeBubbleConvs: state.activeBubbleConvs.filter((c) => c.conversationId !== conversationId)
        }));
      },
      clearActiveBubbleConvs: () => {
        set({ activeBubbleConvs: [] });
      },

      // Reply messages
      setReplyMessage: (conversationId, message) => {
        set((state) => ({
          replyMessages: {
            ...state.replyMessages,
            [conversationId]: message
          }
        }));
      },

      clearReplyMessage: (conversationId) => {
        if (conversationId) {
          set((state) => {
            const { [conversationId]: _, ...rest } = state.replyMessages;
            return { replyMessages: rest };
          });
        } else {
          set({ replyMessages: {} });
        }
      },

      getReplyMessage: (conversationId) => {
        return get().replyMessages[conversationId];
      },

      // Typing indicators
      addTypingUser: (conversationId, userId) => {
        set((state) => {
          const users = new Set(state.typingUsers[conversationId] || []);
          users.add(userId);
          return {
            typingUsers: {
              ...state.typingUsers,
              [conversationId]: users
            }
          };
        });
      },

      removeTypingUser: (conversationId, userId) => {
        set((state) => {
          const users = new Set(state.typingUsers[conversationId] || []);
          users.delete(userId);
          return {
            typingUsers: {
              ...state.typingUsers,
              [conversationId]: users
            }
          };
        });
      },

      clearTypingUsers: (conversationId) => {
        set((state) => {
          const { [conversationId]: _, ...rest } = state.typingUsers;
          return { typingUsers: rest };
        });
      },

      reset: () => {
        set({
          sidebarExpanded: true,
          asideExpanded: false,
          messageSearchDrawerOpen: false,
          activeConversation: null,
          replyMessages: {},
          typingUsers: {},
          activeBubbleConvs: [],
          friends: []
        });
      }
    }),
    {
      name: 'chat-ui-storage',
      storage: createJSONStorage(() => localStorage),
      // Chỉ persist layout preferences
      partialize: (state) => ({
        sidebarExpanded: state.sidebarExpanded,
        asideExpanded: state.asideExpanded,
        activeConversation: state.activeConversation,
        activeBubbleConvs: state.activeBubbleConvs
      })
    }
  )
);
